import type { WorkflowPhase, AgentRole } from '../lib/api';
import { getPhaseColor } from '../lib/utils';

interface Props {
  phase: WorkflowPhase;
  agents: AgentRole[];
  onClick: () => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  readOnly?: boolean;
}

export function WorkflowPhaseRow({ phase, agents, onClick, onDelete, onMoveUp, onMoveDown, isFirst, isLast, readOnly }: Props) {
  const color = getPhaseColor(phase.color).badge;
  const assignedAgents = agents.filter((a) => phase.agents.includes(a.id));

  return (
    <div
      onClick={onClick}
      className={`group flex cursor-pointer items-center gap-4 rounded-lg border border-gray-800 bg-surface-850 px-4 py-3 transition-all hover:border-gray-700 hover:bg-surface-800${phase.disabled ? ' opacity-50' : ''}`}
    >
      <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${color}`}>
        {phase.name}
      </span>

      <p className="min-w-0 flex-1 truncate text-xs text-gray-400">
        {phase.description}
      </p>

      <div className="flex shrink-0 items-center gap-2">
        {phase.disabled ? (
          <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-400">
            Disabled
          </span>
        ) : (
          <>
            {assignedAgents.length > 0 && (
              <span className="text-[10px] text-gray-500">
                {assignedAgents.map((a) => a.name).join(', ')}
              </span>
            )}

            {phase.autoAdvance ? (
              <span className="rounded bg-green-500/10 px-1.5 py-0.5 text-[10px] font-medium text-green-400">
                Auto
              </span>
            ) : (
              <span className="rounded bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-medium text-orange-400">
                Manual
              </span>
            )}
            {phase.createPR && (
              <span className="rounded bg-purple-500/10 px-1.5 py-0.5 text-[10px] font-medium text-purple-400">
                PR
              </span>
            )}
            {phase.autoMergePR && (
              <span className="rounded bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-medium text-indigo-400">
                Merge
              </span>
            )}
            {phase.trackDeployment && (
              <span className="rounded bg-teal-500/10 px-1.5 py-0.5 text-[10px] font-medium text-teal-400">
                Deploy
              </span>
            )}
            {phase.runCliCommand && (
              <span className="rounded bg-yellow-500/10 px-1.5 py-0.5 text-[10px] font-medium text-yellow-400">
                CLI
              </span>
            )}
            {phase.timeoutMinutes && (
              <span className="text-[10px] text-gray-600">{phase.timeoutMinutes}m</span>
            )}
          </>
        )}
      </div>

      {!readOnly && (
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {/* Move Up */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp?.();
            }}
            disabled={isFirst}
            className="rounded p-1 text-gray-600 hover:bg-gray-800 hover:text-gray-400 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-600"
            title="Move up"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </button>

          {/* Move Down */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown?.();
            }}
            disabled={isLast}
            className="rounded p-1 text-gray-600 hover:bg-gray-800 hover:text-gray-400 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-600"
            title="Move down"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Delete */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="rounded p-1 text-gray-600 hover:bg-gray-800 hover:text-gray-400"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
